import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FiCheck,
  FiArrowRight,
  FiShield,
  FiAward,
  FiCheckCircle,
} from 'react-icons/fi';
import {
  GiOilDrum,
  GiFactory,
  GiWheat,
  GiCargoShip,
  GiMining,
  GiElectric,
  GiCarWheel,
  GiCrane,
  GiGears,
  GiTank,
} from 'react-icons/gi';
import { BsShieldCheck } from 'react-icons/bs';

const Industries = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [activeIndustry, setActiveIndustry] = useState(null);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const industries = [
    {
      id: 1,
      name: 'Oil & Petroleum',
      emoji: '⛽',
      icon: GiOilDrum,
      gradient: 'from-vp-grey-700 to-vp-grey-600',
      bgGradient: 'from-vp-cream-100 to-vp-cream-200',
      challenges: [
        'Fire hazards from cleaning solvents',
        'Corrosive maintenance',
        'Strict environmental compliance',
      ],
      solution: 'RU ORA-7 is essential here. Its non-flammable and water-based composition makes it uniquely safe for use on offshore oil rigs and in oil drilling operations. It acts as a powerful degreaser and de-ruster, replacing hazardous fossil fuels typically used for cleaning. ECO friendly non chemical base solution.',
      benefits: [
        'Maximizes operational safety',
        'ECO friendly non chemical base solution',
        'Significantly reduces fossil fuel consumption for cleaning',
        'Maintains equipment integrity',
      ],
    },
    {
      id: 2,
      name: 'Heavy Machinery & Engineering',
      emoji: '🏗️',
      icon: GiCrane,
      gradient: 'from-vp-blue-600 to-vp-blue-500',
      bgGradient: 'from-vp-blue-50 to-vp-cream-100',
      challenges: [
        'Reduced efficiency due to scale buildup in cooling systems',
        'Increased fuel consumption',
        'Corrosive damage from traditional de-scaling water base solution',
      ],
      solution: 'RU DSA-6 is the ideal De-Scaling Agent for radiators, chillers, condensers, and oil coolers. RU ORA-7 provides highly effective carbon removal and degreasing for large engines and equipment.',
      benefits: [
        'Extends equipment life by up to 50%',
        'Improves fuel efficiency',
        'Dramatically reduces maintenance man-hours',
      ],
    },
    {
      id: 3,
      name: 'Agriculture & Horticulture',
      emoji: '🌾',
      icon: GiWheat,
      gradient: 'from-vp-green-600 to-vp-green-500',
      bgGradient: 'from-vp-green-50 to-vp-cream-100',
      challenges: [
        'Widespread invasion of Parthenium Hysterophorus (Gajar Ghas)',
        'Crop loss',
        'Severe health issues (dermatitis, asthma)',
      ],
      solution: 'V-MUKTI 24 is the ONLY Water Base Solution specifically developed for the complete and safe eradication of this menace.',
      benefits: [
        'Reclaims valuable agricultural land',
        'Supports public health initiatives',
        'Non-toxic and harmless to the ecosystem',
      ],
    },
    {
      id: 4,
      name: 'Marine & Shipping',
      emoji: '🚢',
      icon: GiCargoShip,
      gradient: 'from-vp-blue-600 to-vp-blue-700',
      bgGradient: 'from-vp-blue-50 to-vp-blue-100',
      challenges: [
        'Corrosion from salt and humidity',
        'Scale formation in large cooling systems',
        'Heavy oil and carbon deposits in engines',
      ],
      solution: 'RU DSA-6 for ship engine cooling system maintenance; RU ORA-7 for safe de-rusting and degreasing of decks, machinery, and engine rooms.',
      benefits: [
        'Non-corrosive cleaning protects expensive marine assets',
        'Non-flammable cleaning ensures safety at sea',
        'Extends marine equipment lifespan',
      ],
    },
    {
      id: 5,
      name: 'Mining (Surface & Underground)',
      emoji: '⛏️',
      icon: GiMining,
      gradient: 'from-vp-grey-600 to-vp-grey-700',
      bgGradient: 'from-vp-cream-100 to-vp-cream-200',
      challenges: [
        'Extreme carbon and oil buildup on heavy equipment',
        'Strict non-flammability requirements',
        'Ventilation requirements for underground operations',
      ],
      solution: 'RU ORA-7 is critical. Its non-flammable, water-based nature makes it uniquely suitable for cleaning and de-carbonizing equipment used in underground mines where conventional solvents are prohibited.',
      benefits: [
        'Ensures regulatory compliance',
        'Maximizes safety in confined, high-risk environments',
        'Protects heavy earth-moving equipment',
      ],
    },
    {
      id: 6,
      name: 'Power Generation & Utilities',
      emoji: '⚡',
      icon: GiElectric,
      gradient: 'from-vp-green-600 to-vp-green-500',
      bgGradient: 'from-vp-green-50 to-vp-cream-100',
      challenges: [
        'Scale and mineral buildup in cooling towers',
        'Boiler tube and heat exchanger blockages',
        'Severely impacted energy efficiency and uptime',
      ],
      solution: 'RU DSA-6 for efficient, non-corrosive, large-scale descaling of cooling systems to maintain maximum thermal efficiency.',
      benefits: [
        'Optimizes energy consumption',
        'Prevents costly emergency shutdowns',
        'Maintains system blockage-free operation',
      ],
    },
    {
      id: 7,
      name: 'Automotive Service & Repair',
      emoji: '🚗',
      icon: GiCarWheel,
      gradient: 'from-red-600 to-pink-600',
      bgGradient: 'from-red-50 to-pink-50',
      challenges: [
        'Quick, effective carbon removal from engine parts',
        'De-rusting of components',
        'Non-toxic radiator flushing',
      ],
      solution: 'RU ORA-7 for efficient de-carbonizing and parts cleaning; RU DSA-6 for professional radiator maintenance that extends vehicle life.',
      benefits: [
        'Reduces labor time',
        'Offers eco-friendly service option to customers',
        'Professional-grade maintenance',
      ],
    },
    {
      id: 8,
      name: 'Construction & Infrastructure',
      emoji: '🚧',
      icon: GiGears,
      gradient: 'from-slate-600 to-gray-700',
      bgGradient: 'from-slate-50 to-gray-100',
      challenges: [
        'Extreme exposure to dirt, mud, rust',
        'Carbon buildup on construction equipment',
        'Heavy equipment maintenance (cranes, excavators, loaders)',
      ],
      solution: 'RU ORA-7 provides comprehensive cleaning, removing caked-on oil, grease, and rust without damaging paint or metal.',
      benefits: [
        'Extends service life of heavy construction assets',
        'Simplifies fleet maintenance',
        'Protects equipment paint and metal surfaces',
      ],
    },
    {
      id: 9,
      name: 'Manufacturing & Fabrication',
      emoji: '🏭',
      icon: GiFactory,
      gradient: 'from-purple-600 to-indigo-600',
      bgGradient: 'from-purple-50 to-indigo-50',
      challenges: [
        'Oil and grease on factory floors',
        'Rust on metal components',
        'Regulatory pressure for environmentally safe water base solution usage',
      ],
      solution: 'RU ORA-7 for floor degreasing and cleaning fabricated metal parts; RU DSA-6 for plant chiller maintenance.',
      benefits: [
        'Maintains clean, safe working environment',
        'Ensures compliance with environmental standards',
        'Protects production equipment',
      ],
    },
    {
      id: 10,
      name: 'Defense & Railways',
      emoji: '🛡️',
      icon: GiTank,
      gradient: 'from-emerald-700 to-teal-700',
      bgGradient: 'from-emerald-50 to-teal-50',
      challenges: [
        'Maintaining peak operational readiness',
        'Critical, expensive fleet equipment (locomotives, heavy vehicles)',
        'Need for standardized, non-corrosive maintenance',
      ],
      solution: 'RU DSA-6 for cooling system maintenance; RU ORA-7 for heavy carbon and grease removal from engines and undercarriages.',
      benefits: [
        'Guaranteed non-corrosive cleaning',
        'Protects high-value assets',
        'Maximizes equipment uptime',
      ],
    },
  ];

  const certifications = [
    { name: 'ISO 9001', desc: 'Quality Management', icon: FiAward },
    { name: 'ISO 14001', desc: 'Environmental Management', icon: FiShield },
    { name: 'GMP', desc: 'Good Manufacturing Practices', icon: BsShieldCheck },
    { name: 'REACH', desc: 'Water Base Solution Compliance', icon: FiCheckCircle },
  ];

  // Molecular pattern
  const MolecularPattern = () => (
    <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <pattern id="molecule-industries" x="0" y="0" width="200" height="200" patternUnits="userSpaceOnUse">
          <circle cx="20" cy="20" r="3" fill="currentColor" opacity="0.3" />
          <circle cx="80" cy="40" r="4" fill="currentColor" opacity="0.2" />
          <circle cx="140" cy="70" r="2" fill="currentColor" opacity="0.4" />
          <line x1="20" y1="20" x2="80" y2="40" stroke="currentColor" strokeWidth="0.5" opacity="0.2" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#molecule-industries)" />
    </svg>
  );

  return (
    <div className="min-h-screen overflow-hidden bg-white">
      {/* Section 1: Page Header */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden pt-32">
        {/* Dynamic Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-vp-cream-100 via-white to-vp-cream-200">
          <div 
            className="absolute inset-0 opacity-20"
            style={{
              background: `radial-gradient(circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(0, 135, 81, 0.15), transparent 50%)`,
            }}
          />
        </div>

        {/* Molecular Pattern */}
        <div className="absolute inset-0 text-vp-green-400 opacity-10">
          <MolecularPattern />
        </div>

        {/* Floating industry icons */}
        <div className="absolute inset-0 overflow-hidden">
          {[GiOilDrum, GiFactory, GiWheat, GiCargoShip, GiMining, GiElectric].map((Icon, i) => (
            <motion.div
              key={i}
              className="absolute"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, -50, 0],
                x: [0, Math.random() * 30 - 15, 0],
                scale: [1, 1.3, 1],
                opacity: [0.2, 0.5, 0.2],
              }}
              transition={{
                duration: 7 + Math.random() * 3,
                repeat: Infinity,
                delay: Math.random() * 3,
              }}
            >
              <Icon className="w-12 h-12 text-vp-green-400/30" />
            </motion.div>
          ))}
        </div>

        {/* Content */}
        <div className="relative z-10 container mx-auto px-4 lg:px-8">
          <motion.div
            className="max-w-5xl mx-auto text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Badge */}
            <motion.div
              className="inline-flex items-center space-x-3 bg-gradient-to-r from-vp-green-100 to-vp-blue-100 px-6 py-3 rounded-full text-vp-green-700 mb-8 border border-vp-green-400/30 shadow-md"
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <GiFactory className="text-vp-green-600 text-2xl" />
              <span className="font-semibold">10+ Industry Sectors</span>
            </motion.div>

            {/* Headline */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-vp-grey-900 mb-8 leading-tight">
              Specialized, Eco-Friendly Solutions
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-vp-green-600 via-vp-blue-600 to-vp-green-600">
                for High-Demand Sectors
              </span>
            </h1>

            {/* Sub-headline */}
            <motion.p
              className="text-xl md:text-2xl text-black mb-8 max-w-4xl mx-auto leading-relaxed"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              VP Chem Technologies provides{' '}
              <span className="text-indigo-600 font-bold">premium-grade, biodegradable water base solution</span> tailored to
              meet the exacting standards of diverse industries, enhancing efficiency and sustainability globally.
            </motion.p>

            <motion.p
              className="text-lg md:text-xl text-black mb-12 max-w-4xl mx-auto leading-relaxed"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              <span className="text-vp-green-600 font-semibold">Eco friendly non chemical base compound and non hazard solution</span>
            </motion.p>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
            >
              <Link
                to="/contact"
                className="group inline-flex items-center space-x-3 px-12 py-6 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold text-xl rounded-full hover:shadow-2xl hover:shadow-indigo-500/50 transform hover:scale-105 transition-all duration-300"
              >
                <span>Find the Right Solution for Your Business</span>
                <FiArrowRight className="group-hover:translate-x-2 transition-transform" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Section 2: Core Industry Sectors */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100 relative">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {/* Section Header */}
            <div className="text-center mb-16">
              <motion.span
                className="inline-block px-6 py-2 bg-gradient-to-r from-indigo-100 to-purple-100 rounded-full text-indigo-700 font-semibold mb-6"
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ type: "spring" }}
              >
                🏭 Core Industry Sectors
              </motion.span>
              
              <h2 className="text-5xl md:text-6xl font-black text-slate-900 mb-6">
                Tackling Unique Challenges Across{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">
                  Critical Global Sectors
                </span>
              </h2>
              
              <p className="text-xl md:text-2xl text-black max-w-4xl mx-auto leading-relaxed">
                Our comprehensive product range offers{' '}
                <span className="font-bold text-indigo-600">unparalleled performance</span> while prioritizing safety
                and compliance.
              </p>
            </div>

            {/* Industries Grid */}
            <div className="space-y-16 max-w-7xl mx-auto">
              {industries.map((industry, index) => (
                <motion.div
                  key={industry.id}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 items-center`}
                >
                  {/* Icon Side */}
                  <motion.div
                    className="flex-shrink-0"
                    whileHover={{ scale: 1.1, rotate: index % 2 === 0 ? 5 : -5 }}
                    onHoverStart={() => setActiveIndustry(industry.id)}
                    onHoverEnd={() => setActiveIndustry(null)}
                  >
                    <div className={`w-64 h-64 bg-gradient-to-br ${industry.gradient} rounded-3xl flex flex-col items-center justify-center shadow-2xl relative overflow-hidden`}>
                      {/* Animated glow */}
                      <motion.div
                        className="absolute inset-0 bg-white opacity-0"
                        animate={{
                          opacity: activeIndustry === industry.id ? 0.2 : 0,
                        }}
                        transition={{ duration: 0.3 }}
                      />
                      
                      <industry.icon className="w-32 h-32 text-white mb-4 relative z-10" />
                      <span className="text-6xl relative z-10">{industry.emoji}</span>
                    </div>
                  </motion.div>

                  {/* Content Side */}
                  <div className={`flex-1 bg-gradient-to-br ${industry.bgGradient} rounded-3xl p-10 shadow-xl border-2 border-slate-200`}>
                    {/* Title */}
                    <h3 className="text-4xl md:text-5xl font-black text-slate-900 mb-6">
                      {industry.name}
                    </h3>

                    {/* Challenges */}
                    <div className="mb-6">
                      <h4 className="text-xl font-bold text-red-700 mb-3 flex items-center space-x-2">
                        <span className="w-2 h-2 bg-red-500 rounded-full" />
                        <span>Key Challenges:</span>
                      </h4>
                      <ul className="space-y-2">
                        {industry.challenges.map((challenge, idx) => (
                          <li key={idx} className="flex items-start space-x-2 text-black">
                            <span className="text-red-500 mt-1">•</span>
                            <span>{challenge}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Solution */}
                    <div className="mb-6 bg-white/70 backdrop-blur-sm p-6 rounded-2xl border-2 border-indigo-200">
                      <h4 className="text-xl font-bold text-indigo-700 mb-3 flex items-center space-x-2">
                        <span className="w-2 h-2 bg-indigo-500 rounded-full" />
                        <span>VP Tech Solution:</span>
                      </h4>
                      <p className="text-black leading-relaxed">{industry.solution}</p>
                    </div>

                    {/* Benefits */}
                    <div>
                      <h4 className="text-xl font-bold text-green-700 mb-3 flex items-center space-x-2">
                        <span className="w-2 h-2 bg-green-500 rounded-full" />
                        <span>Benefits:</span>
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {industry.benefits.map((benefit, idx) => (
                          <motion.div
                            key={idx}
                            initial={{ opacity: 0, scale: 0.9 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: idx * 0.1 }}
                            className="flex items-start space-x-2 bg-white/70 backdrop-blur-sm px-4 py-3 rounded-xl border border-green-200"
                          >
                            <FiCheck className="text-green-600 flex-shrink-0 mt-0.5 text-lg" />
                            <span className="text-black font-medium">{benefit}</span>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Section 3: Commitment to Compliance */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-vp-green-700 via-teal-600 to-vp-blue-600 text-white relative overflow-hidden">
        {/* Animated grid */}
        <div className="absolute inset-0 opacity-5">
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: `linear-gradient(to right, rgba(255, 255, 255, 0.3) 1px, transparent 1px),
                                linear-gradient(to bottom, rgba(255, 255, 255, 0.3) 1px, transparent 1px)`,
              backgroundSize: '50px 50px',
            }}
          />
        </div>

        {/* Floating particles */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(30)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-white/40 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, -100, 0],
                opacity: [0, 0.5, 0],
              }}
              transition={{
                duration: 3 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 3,
              }}
            />
          ))}
        </div>

        <div className="relative container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {/* Section Header */}
            <div className="text-center mb-16">
              <motion.span
                className="inline-block px-6 py-2 bg-white/20 backdrop-blur-xl rounded-full text-white font-semibold mb-6 border border-white/30"
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ type: "spring" }}
              >
                🏆 Commitment to Compliance
              </motion.span>
              
              <h2 className="text-5xl md:text-6xl lg:text-7xl font-black mb-8">
                Quality & Compliance{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-vp-cream-100 to-white">
                  Across All Sectors
                </span>
              </h2>
              
              <p className="text-xl md:text-2xl text-white max-w-4xl mx-auto leading-relaxed">
                We assure quality and compliance, allowing you to{' '}
                <span className="text-vp-cream-100 font-bold">focus on operations</span> while we manage water base solution
                safety and performance. Our products are backed by the rigorous standards of our manufacturing team:
              </p>
            </div>

            {/* Certifications Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto mb-16">
              {certifications.map((cert, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.15 }}
                  whileHover={{ y: -10, scale: 1.05 }}
                  className="group relative"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-white to-vp-cream-100 opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-500 rounded-3xl" />
                  
                  <div className="relative bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 text-center group-hover:bg-white/10 group-hover:border-white/30 transition-all duration-500">
                    <motion.div
                      className="w-20 h-20 bg-gradient-to-br from-white to-vp-cream-100 rounded-2xl flex items-center justify-center mx-auto mb-6"
                      whileHover={{ rotate: 360 }}
                      transition={{ duration: 0.6 }}
                    >
                      <cert.icon className="w-10 h-10 text-vp-green-600" />
                    </motion.div>
                    
                    <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
                      {cert.name}
                    </h3>
                    <p className="text-vp-cream-100 text-lg">
                      {cert.desc}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Additional compliance text */}
            <motion.div
              className="max-w-4xl mx-auto bg-white/5 backdrop-blur-xl border border-white/20 rounded-3xl p-10 mb-16"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
            >
              <div className="flex items-start space-x-6">
                <div className="w-16 h-16 bg-gradient-to-br from-white to-vp-cream-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <BsShieldCheck className="w-8 h-8 text-vp-green-600" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white mb-3">
                    Comprehensive Quality Assurance
                  </h3>
                  <p className="text-white leading-relaxed text-lg">
                    Every product undergoes rigorous testing and quality control processes, ensuring consistent
                    performance and adherence to international safety standards.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* CTA */}
            <motion.div
              className="text-center"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              <Link
                to="/contact"
                className="group inline-flex items-center space-x-3 px-12 py-6 bg-white text-vp-green-700 font-bold text-xl rounded-full hover:bg-vp-cream-100 transform hover:scale-105 transition-all duration-300 shadow-2xl"
              >
                <BsShieldCheck className="text-2xl" />
                <span>Speak to an Industry Specialist</span>
                <FiArrowRight className="group-hover:translate-x-2 transition-transform" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-24 md:py-32 bg-gradient-to-r from-vp-green-600 via-teal-600 to-vp-blue-600 relative overflow-hidden">
        {/* Particles */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(40)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-white rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, -150, 0],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 4 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 4,
              }}
            />
          ))}
        </div>

        <div className="relative container mx-auto px-4 lg:px-8">
          <motion.div
            className="max-w-5xl mx-auto text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-black text-white mb-8">
              Ready to Transform Your Operations?
            </h2>
            
            <p className="text-xl md:text-2xl text-white mb-12 max-w-3xl mx-auto leading-relaxed">
              Partner with VP Chem Technologies for specialized solutions tailored to your industry's unique
              challenges
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <Link
                to="/contact"
                className="group w-full sm:w-auto px-12 py-6 bg-white text-vp-green-700 font-bold text-xl rounded-2xl hover:bg-vp-cream-100 transform hover:-translate-y-2 hover:scale-105 transition-all duration-300 shadow-2xl flex items-center justify-center space-x-3"
              >
                <span>Schedule a Consultation</span>
                <FiArrowRight className="group-hover:translate-x-2 transition-transform" />
              </Link>
              
              <Link
                to="/products"
                className="group w-full sm:w-auto px-12 py-6 bg-transparent border-3 border-white text-white font-bold text-xl rounded-2xl hover:bg-white hover:text-vp-green-700 transform hover:-translate-y-2 hover:scale-105 transition-all duration-300 flex items-center justify-center space-x-3"
              >
                <span>Explore Our Products</span>
                <FiArrowRight className="group-hover:translate-x-2 transition-transform" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Industries;



